import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { demoChoices, clearDemoPlan } from "@/lib/demo";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppShell } from "@/components/AppShell";
import { MacroDonut, MacroBar } from "@/components/Macros";
import { DailyTracking } from "@/components/DailyTracking";
import { TomorrowCard } from "@/components/TomorrowCard";
import { JournalCard } from "@/components/journal/JournalCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, RefreshCw, ArrowRight, Target, ChevronRight } from "lucide-react";

const DAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [generating, setGenerating] = useState(false);

  const { data: goalsData } = useQuery({ queryKey: ["goals"], queryFn: async () => (await api.get("/nutrition/goals")).data });
  const { data: planData } = useQuery({ queryKey: ["mealplan"], queryFn: async () => (await api.get("/meal-plan")).data });

  const goals = goalsData?.goals;
  const complete = goalsData?.complete;
  const plan = planData?.exists ? planData : null;
  const applyingDemo = useRef(false);
  useEffect(() => {
    if (!planData) return;
    const choices = demoChoices();
    if (!choices.length || applyingDemo.current) return;
    applyingDemo.current = true;
    const ready = plan ? Promise.resolve() : api.post("/meal-plan/generate").then(() => qc.invalidateQueries({ queryKey: ["mealplan"] }));
    ready.then(() => api.post("/meal-plan/apply-choices", { choices })).then(({ data }) => {
      clearDemoPlan();
      if (data.applied) {
        qc.invalidateQueries({ queryKey: ["mealplan"] }); qc.invalidateQueries({ queryKey: ["grocery"] }); qc.invalidateQueries({ queryKey: ["tracking"] });
        toast.success(t("dm_applied", { count: data.applied }));
      }
    }).catch(() => {});
  }, [planData?.exists]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/meal-plan/generate");
      await qc.invalidateQueries({ queryKey: ["mealplan"] });
      toast.success(t("view_plan"));
      nav("/meal-plan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setGenerating(false); }
  };

  const todayKey = DAY_KEYS[new Date().getDay()];
  const todayPlan = plan?.plan?.days?.find((x) => x.day === todayKey) || plan?.plan?.days?.[0];

  return (
    <AppShell>
      <div className="chenia-fade-up">
        <p className="text-sm uppercase tracking-widest text-muted-foreground font-semibold">{t("today")}</p>
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
          {t("welcome_back")}, {user?.first_name} 🌿
        </h1>
      </div>

      {!complete ? (
        <div className="chenia-card p-8 mt-8 text-center" data-testid="dashboard-incomplete">
          <span className="grid place-items-center h-14 w-14 rounded-2xl bg-secondary text-primary mx-auto"><Target className="h-6 w-6" /></span>
          <h2 className="mt-4 text-xl font-semibold">{t("complete_profile")}</h2>
          <Link to="/onboarding">
            <Button className="mt-5 rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="dashboard-complete-onboarding">
              {t("complete_onboarding")} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-5 mt-8">
          {/* Target card */}
          <div className="chenia-card p-6 lg:col-span-1 flex flex-col items-center" data-testid="dashboard-target">
            <p className="self-start text-sm uppercase tracking-widest text-muted-foreground font-semibold mb-2">{t("daily_target")}</p>
            <MacroDonut value={goals.target_calories} max={goals.target_calories} label={t("calories")} sublabel={t("kcal")} />
            <div className="w-full mt-5 space-y-3.5">
              <MacroBar label={t("protein")} value={goals.protein_g} unit="g" pct={goals.protein_pct} color="#C87D65" />
              <MacroBar label={t("carbs")} value={goals.carbs_g} unit="g" pct={goals.carbs_pct} color="#A8BBA2" />
              <MacroBar label={t("fat")} value={goals.fat_g} unit="g" pct={goals.fat_pct} color="#E8B4B8" />
            </div>
            <Link to="/goals" className="mt-5 self-start text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1" data-testid="dashboard-view-goals">
              {t("view_goals")} <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Coaching + plan */}
          <div className="lg:col-span-2 space-y-5 min-w-0">
            <div className="chenia-card p-6 bg-gradient-to-br from-chenia-rosesoft to-card" data-testid="dashboard-coaching">
              <div className="flex items-center gap-2 text-chenia-terracottadark font-semibold">
                <Sparkles className="h-4 w-4" /> {t("coaching")}
              </div>
              <p className="mt-3 text-foreground/90 leading-relaxed">
                {plan?.coaching_summary || t("no_plan_desc")}
              </p>
            </div>

            {plan ? (
              <>
                <DailyTracking plan={plan.plan} todayPlan={todayPlan} />
                <JournalCard />
                <TomorrowCard />
                <div className="flex gap-2">
                  <Link to="/meal-plan" className="flex-1">
                    <Button className="w-full rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="dashboard-view-plan">
                      {t("view_plan")} <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                  <Button variant="outline" onClick={generate} disabled={generating} className="rounded-full" data-testid="dashboard-regenerate">
                    <RefreshCw className={`h-4 w-4 mr-1 ${generating ? "animate-spin" : ""}`} /> {t("regenerate")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="chenia-card p-8 text-center" data-testid="dashboard-no-plan">
                  <h2 className="text-xl font-semibold">{t("no_plan_title")}</h2>
                  <p className="mt-2 text-muted-foreground">{t("no_plan_desc")}</p>
                  <Button onClick={generate} disabled={generating} className="mt-5 rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="dashboard-generate">
                    {generating ? (<><RefreshCw className="h-4 w-4 mr-1 animate-spin" /> {t("generating")}</>) : (<><Sparkles className="h-4 w-4 mr-1" /> {t("generate_plan")}</>)}
                  </Button>
                </div>
                <JournalCard />
              </>
            )}
          </div>
        </div>
      )}

      <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{t("disclaimer")}</p>
    </AppShell>
  );
}
